import api from './api';

const API_SCOPE = 'api://6069b616-c7f2-4978-9f9c-180ec12f1844/access_as_user';

/**
 * Acquire an API-scoped access token for dashboard endpoints.
 * Uses the same scope as support/analytics/nova/favorites.
 * MSAL caches the access token and returns it without a network call
 * until it's within ~5min of expiry (typically ~1h lifetime).
 */
export async function getDashboardToken(msalInstance) {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;
  try {
    const resp = await msalInstance.acquireTokenSilent({
      scopes: [API_SCOPE],
      account: accounts[0],
    });
    return resp.accessToken;
  } catch {
    try {
      const resp = await msalInstance.acquireTokenPopup({ scopes: [API_SCOPE] });
      return resp.accessToken;
    } catch {
      return null;
    }
  }
}

function authCfg(headers) {
  return headers ? { headers: { Authorization: `Bearer ${headers}` } } : {};
}

export async function fetchDashboardStats(token) {
  const { data } = await api.get('/api/dashboard/stats', authCfg(token));
  return data;
}

export async function fetchAllSupplierProducts(params = {}, token) {
  const { data } = await api.get('/api/dashboard/supplier-products', { params, ...authCfg(token) });
  return data;
}

export async function fetchProductDetail(productId, token) {
  const { data } = await api.get(`/api/dashboard/supplier-products/${encodeURIComponent(productId)}`, authCfg(token));
  return data;
}

export async function fetchTopAtcCategories(token) {
  const { data } = await api.get('/api/dashboard/charts/top-atc-categories', authCfg(token));
  return data;
}

export async function fetchTopSuppliersInTopAtc(token) {
  const { data } = await api.get('/api/dashboard/charts/top-suppliers-in-top-atc', authCfg(token));
  return data;
}

export async function fetchTopSuppliersByInn(token) {
  const { data } = await api.get('/api/dashboard/charts/top-suppliers-by-inn', authCfg(token));
  return data;
}

export async function fetchLatestSuppliers(token) {
  const { data } = await api.get('/api/dashboard/charts/latest-suppliers', authCfg(token));
  return data;
}

export async function fetchTopCountries(token) {
  const { data } = await api.get('/api/dashboard/charts/top-countries', authCfg(token));
  return data;
}

export async function fetchSupplierRegions(token) {
  const { data } = await api.get('/api/dashboard/charts/supplier-regions', authCfg(token));
  return data;
}

export async function fetchDossierAvailability(token) {
  const { data } = await api.get('/api/dashboard/charts/dossier-availability', authCfg(token));
  return data;
}

export async function fetchContractAlerts(token) {
  const { data } = await api.get('/api/dashboard/contract-alerts', authCfg(token));
  return data;
}

export async function fetchMoleculesFewestSuppliers(token) {
  const { data } = await api.get('/api/dashboard/charts/molecules-fewest-suppliers', authCfg(token));
  return data;
}

export async function fetchMoleculesMostSuppliers(token) {
  const { data } = await api.get('/api/dashboard/charts/molecules-most-suppliers', authCfg(token));
  return data;
}

// ── Supplier Contracts ──

export async function fetchContracts(token) {
  const { data } = await api.get('/api/supplier-contracts', authCfg(token));
  return data;
}

export async function fetchContractMolecules(contractId, token) {
  const { data } = await api.get(`/api/supplier-contracts/${contractId}/molecules`, authCfg(token));
  return data;
}

export async function fetchSupplierMolecules(supplierId, token) {
  const { data } = await api.get(`/api/supplier-contracts/suppliers/${encodeURIComponent(supplierId)}/molecules`, authCfg(token));
  return data;
}

export async function createContract(body, token) {
  const { data } = await api.post('/api/supplier-contracts', body, authCfg(token));
  return data;
}

export async function uploadContractDocument(contractId, file, token) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post(
    `/api/supplier-contracts/${contractId}/document`,
    fd,
    { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function updateContract(contractId, formFields, molecules, documentAction, file, token) {
  const fd = new FormData();
  fd.append('contact_name', formFields.contact_name);
  fd.append('contact_email', formFields.contact_email);
  fd.append('contact_phone', formFields.contact_phone);
  fd.append('signed_date', formFields.signed_date || '');
  fd.append('start_date', formFields.start_date);
  fd.append('end_date', formFields.end_date);
  fd.append('molecules', JSON.stringify(molecules));
  if (documentAction) fd.append('document_action', documentAction);
  if (file) fd.append('file', file);
  const { data } = await api.put(
    `/api/supplier-contracts/${contractId}`,
    fd,
    { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}
